import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

let client: SupabaseClient | null = null;

export function getSupabase(): SupabaseClient {
  if (!client) {
    client = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
        flowType: 'pkce',
      },
    });
  }
  return client;
}

export const supabase = getSupabase();

/**
 * 带重试的 supabase 查询包装器
 * @param fn 执行查询的函数，返回 supabase query result
 * @param retries 最大重试次数（默认 3）
 * @param delayMs 初始延迟（默认 1000ms，指数退避）
 */
export async function withRetry<T>(
  fn: () => Promise<{ data: T | null; error: unknown }>,
  retries = 3,
  delayMs = 1000,
): Promise<{ data: T | null; error: unknown }> {
  let lastResult: { data: T | null; error: unknown } = { data: null, error: null };
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      lastResult = await fn();
      if (!lastResult.error) return lastResult;
      // 非网络错误（如 RLS 拒绝）不重试
      const errMsg = String((lastResult.error as { message?: string })?.message || lastResult.error).toLowerCase();
      const isTransient = errMsg.includes('network') || errMsg.includes('fetch') || errMsg.includes('timeout') || errMsg.includes('failed to fetch') || errMsg.includes('econnreset');
      if (!isTransient) return lastResult;
    } catch (e) {
      lastResult = { data: null, error: e };
    }
    if (attempt < retries - 1) {
      await new Promise((r) => setTimeout(r, delayMs * 2 ** attempt));
    }
  }
  return lastResult;
}